/* @license
 * Copyright 2025 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { LinearFilter, NearestFilter, SRGBColorSpace, Texture, } from 'three';
import { promisifyWorker } from './promisify-worker.js';
const MIN_DELAY_MS = 100;
// Create worker - use path that works when served from packages/model-viewer/
// The worker is compiled to lib/workers/gifparsing.worker.js
// When code is at lib/utilities/gif-texture.js, worker is at lib/workers/gifparsing.worker.js
// So relative path ../workers/gifparsing.worker.js should work
const getWorkerPath = () => {
    // When bundled, import.meta.url points to the bundle location (e.g., /dist/model-viewer.js or /model-viewer.js)
    // The worker is copied to /dist/workers/ during build, but in Cloudflare Pages it's at /workers/
    // Use relative path from bundle location to worker
    try {
        const baseUrl = new URL(import.meta.url);
        const basePath = baseUrl.pathname;
        // If the bundle is in /dist/, worker is at /dist/workers/
        // If the bundle is at root (Cloudflare Pages), worker is at /workers/
        if (basePath.includes('/dist/')) {
            // Development or local build - worker is at /dist/workers/
            return '/dist/workers/gifparsing.worker.js';
        }
        else {
            // Production deployment (Cloudflare Pages) - worker is at /workers/
            // Use absolute path from root
            return new URL('/workers/gifparsing.worker.js', baseUrl.origin).href;
        }
    }
    catch (e) {
        // Fallback: try root path (for Cloudflare Pages)
        return '/workers/gifparsing.worker.js';
    }
};
const workerPath = getWorkerPath();
const worker = new Worker(workerPath, { type: 'module' });
const parseGIF = promisifyWorker(worker);
const loadImageFromURL = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load GIF frame: ${src}`));
        img.src = src;
    });
};
export class GIFTexture extends Texture {
    constructor(frames, delays, disposals, frameUrls) {
        const canvas = document.createElement('canvas');
        super(canvas);
        this.frameIndex = 0;
        this.isPlaying = false;
        this.frameStartTime = performance.now();
        this.ctx = null;
        this.frameUrls = [];
        this.frames = frames;
        this.delays = delays;
        this.disposals = disposals;
        this.frameUrls = frameUrls;
        if (frames[0]) {
            canvas.width = frames[0].width;
            canvas.height = frames[0].height;
        }
        else {
            canvas.width = 1;
            canvas.height = 1;
        }
        this.ctx = canvas.getContext('2d');
        this.generateMipmaps = false;
        this.isVideoTexture = true;
        this.minFilter = NearestFilter;
        this.colorSpace = SRGBColorSpace;
        this.resetToFirstFrame();
    }
    play() {
        if (this.isPlaying) {
            return;
        }
        this.isPlaying = true;
        this.frameStartTime = performance.now();
    }
    pause() {
        this.isPlaying = false;
    }
    resetToFirstFrame() {
        this.frameIndex = 0;
        this.frameStartTime = performance.now();
        this.drawCurrentFrame(true);
    }
    drawCurrentFrame(force = false) {
        if (!this.ctx || !this.frames[this.frameIndex]) {
            return;
        }
        if (force || this.disposals[this.frameIndex] === 2) {
            this.ctx.clearRect(0, 0, this.image.width, this.image.height);
        }
        this.ctx.drawImage(this.frames[this.frameIndex], 0, 0, this.image.width, this.image.height);
        this.needsUpdate = true;
    }
    update() {
        var _a;
        if (!this.isPlaying || !this.frames.length || !this.ctx) {
            return;
        }
        const now = performance.now();
        const delay = (_a = this.delays[this.frameIndex]) !== null && _a !== void 0 ? _a : MIN_DELAY_MS;
        const elapsed = now - this.frameStartTime;
        const requiredDelay = Math.max(delay, MIN_DELAY_MS);
        if (elapsed < requiredDelay) {
            return;
        }
        this.frameStartTime = now;
        this.frameIndex = (this.frameIndex + 1) % this.frames.length;
        this.drawCurrentFrame();
    }
    dispose() {
        super.dispose();
        this.frames = [];
        this.delays = [];
        this.disposals = [];
        this.ctx = null;
        this.frameUrls.forEach((url) => URL.revokeObjectURL(url));
        this.frameUrls = [];
    }
}
export const createGIFTexture = async (url) => {
    const response = await fetch(url, { mode: 'cors' });
    const arrayBuffer = await response.arrayBuffer();
    const result = await parseGIF(arrayBuffer, [arrayBuffer]);
    const delays = result.delayTimes.map((delay) => Math.max(delay || MIN_DELAY_MS, MIN_DELAY_MS));
    const frames = await Promise.all(result.frames.map(loadImageFromURL));
    const texture = new GIFTexture(frames, delays, result.disposals, result.frames);
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.play();
    return texture;
};
//# sourceMappingURL=gif-texture.js.map